import React, { useState, useEffect } from "react";
import { Accordion, Button, Container, Row, Col, ListGroup, Badge } from "react-bootstrap";
import AttachmentList from "./AttachmentList";
import { formatLocalDate } from "../utils/dateUtils";
import styles from "./AttachmentBrowser.module.css";
import { getIconForContentType } from "../utils/getIconForContentType";

const truncate = (str, maxLength = 25) =>
    str.length > maxLength ? str.slice(0, maxLength) + "…" : str;

const AttachmentBrowser = ({ selectedPatient, fhirClient, onAttach, onClose }) => {
    const patientId = selectedPatient?.id;
    const resourceTypes = ["DocumentReference", "DiagnosticReport", "Observation", "Media"];
    const [entries, setEntries] = useState([]);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [selectedAttachments, setSelectedAttachments] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchEntries = async () => {
        setLoading(true);
        const results = [];

        const fetchers = resourceTypes.map(async (type) => {
            const url = `${type}?subject=Patient/${patientId}`;
            console.log(`📡 Fetching ${type} from FHIR: ${url}`);

            try {
                const bundle = await fhirClient.request(url, {
                    pageLimit: 1,
                    flat: false,
                });

                const typeEntries = Array.isArray(bundle.entry) ? bundle.entry : [];
                console.log(`✅ Fetched ${typeEntries.length} entries for ${type}`);

                typeEntries.forEach(({ resource }) => {
                    if (!resource?.id) return;

                    if (type === "Observation") {
                        if (resource.valueAttachment?.url) {
                            results.push({
                                title: resource.valueAttachment.title || `Observation ${resource.id}`,
                                sourceType: "Observation",
                                resourceId: resource.id,
                                index: 0,
                                fileType: "Observation",
                                resource: {
                                    resourceType: "Binary",
                                    id: resource.id,
                                    contentType: resource.valueAttachment.contentType,
                                    title: resource.valueAttachment.title,
                                    url: resource.valueAttachment.url,
                                },
                            });
                        }

                        if (Array.isArray(resource.component)) {
                            resource.component.forEach((comp, i) => {
                                const attach = comp.valueAttachment;
                                if (attach?.url) {
                                    results.push({
                                        title: attach.title || `Observation ${resource.id} [${i}]`,
                                        sourceType: "Observation",
                                        resourceId: `${resource.id}-${i}`,
                                        index: i,
                                        fileType: "Observation",
                                        resource: {
                                            resourceType: "Binary",
                                            id: `${resource.id}-${i}`,
                                            contentType: attach.contentType,
                                            title: attach.title,
                                            url: attach.url,
                                        },
                                    });
                                }
                            });
                        }

                        return;
                    }

                    if (type === "Media") {
                        const attachment = resource.content?.attachment || resource.content;
                        if (attachment?.url) {
                            results.push({
                                title: attachment.title || `Media ${resource.id}`,
                                sourceType: "Media",
                                resourceId: resource.id,
                                index: 0,
                                fileType: "Media",
                                contentType: attachment.contentType,
                                url: attachment.url,
                                title: attachment.title,
                                resource, // original Media resource
                            });
                        }
                        return;
                    }

                    if (type === "DocumentReference") {
                        if (Array.isArray(resource.content)) {
                            resource.content.forEach((item, i) => {
                                const attachment = item.attachment;
                                if (attachment?.url) {
                                    results.push({
                                        title: attachment.title || attachment.description || `Document ${resource.id} [${i}]`,
                                        description: attachment.description,
                                        sourceType: "DocumentReference",
                                        resourceId: `${resource.id}-${i}`,
                                        index: i,
                                        fileType: "DocumentReference",
                                        contentType: attachment.contentType,
                                        url: attachment.url,
                                        resource, // original DocumentReference
                                    });
                                }
                            });
                        }
                        return;
                    }
                });
            } catch (err) {
                console.error(`❌ Error fetching ${type} at ${url}:`, err);
            }
        });

        await Promise.all(fetchers);
        setEntries(results);
        setLoading(false);
    };

    useEffect(() => {
        if (patientId && fhirClient) fetchEntries();
    }, [patientId, fhirClient]);

    const hasAttachments = (resource, sourceType) => {
        if (!resource) return false;

        if (sourceType === "Observation") {
            return (
                resource.valueAttachment?.url ||
                (Array.isArray(resource.component) &&
                    resource.component.some((c) => c.valueAttachment?.url))
            );
        }

        if (sourceType === "Media") {
            const c = resource.content;
            const attachment = c?.attachment || c;
            return !!attachment?.url;
        }

        return (
            (Array.isArray(resource.content) && resource.content.length > 0) ||
            (Array.isArray(resource.presentedForm) && resource.presentedForm.length > 0) ||
            (Array.isArray(resource.attachment) && resource.attachment.length > 0) ||
            (Array.isArray(resource.contained) &&
                resource.contained.some(
                    (c) => Array.isArray(c.content) && c.content.some((i) => i.attachment?.url)
                ))
        );
    };

    const groupedEntries = resourceTypes.reduce((acc, type) => {
        acc[type] = entries.filter(
            (entry) => entry.sourceType === type && hasAttachments(entry.resource, type)
        );
        return acc;
    }, {});

    const getAttachmentCount = (type) => groupedEntries[type]?.length || 0;

    const toggleSelected = (wrapped) => {
        const { resourceId, index, resource } = wrapped;
        console.log("Wrapped selection:", wrapped);

        const binaryRef = resource?.content?.[0]?.attachment?.url;
        const binaryId = binaryRef?.startsWith("Binary/") ? binaryRef.split("/")[1] : null;

        const binaryWrapped = binaryId
            ? {
                ...wrapped,
                resourceId: binaryId,
                resource: {
                    resourceType: "Binary",
                    id: binaryId,
                    contentType: resource.content[0].attachment.contentType,
                    title: resource.content[0].attachment.title,
                },
            }
            : wrapped;

        setSelectedAttachments((prev) => {
            const exists = prev.some(
                (a) => a.resourceId === binaryWrapped.resourceId && a.index === binaryWrapped.index
            );
            return exists
                ? prev.filter(
                    (a) => !(a.resourceId === binaryWrapped.resourceId && a.index === binaryWrapped.index)
                )
                : [...prev, binaryWrapped];
        });
    };

    const removeAttachment = (resourceId, index) => {
        setSelectedAttachments((prev) =>
            prev.filter((a) => !(a.resourceId === resourceId && a.index === index))
        );
    };

    const attachAndClose = () => {
        console.log("Wrapped Attachment Payload:", selectedAttachments);
        onAttach?.((prev) => [...prev, ...selectedAttachments]);
        onClose?.();
    };

    return (
        <Container fluid className="p-0">
            <Row className="mb-2">
                <Col>
                    <Button
                        variant="primary"
                        onClick={attachAndClose}
                        disabled={selectedAttachments.length === 0}
                    >
                        Attach ({selectedAttachments.length})
                    </Button>
                </Col>
            </Row>

            {selectedAttachments.length > 0 && (
                <Row className="mb-3">
                    <Col>
                        <div className="d-flex flex-wrap">
                            {selectedAttachments.map(
                                ({ fileType, resourceId, index, title, contentType, url }) => (
                                    <Badge
                                        key={`${resourceId}-${index}`}
                                        bg="info"
                                        className="me-2 mb-1 d-flex align-items-center"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => removeAttachment(resourceId, index)}
                                    >
                                        {getIconForContentType(fileType, "Browser")}
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ms-1 text-white text-decoration-underline"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {truncate(title)}
                                        </a>
                                        <span className="ms-1 fw-bold">×</span>
                                    </Badge>
                                )
                            )}
                        </div>
                    </Col>
                </Row>
            )}

            <Row>
                <Col md={6}>
                    <Accordion>
                        {resourceTypes.map((type, idx) => (
                            <Accordion.Item eventKey={idx.toString()} key={type}>
                                <Accordion.Header>
                                    {type} ({getAttachmentCount(type)})
                                </Accordion.Header>
                                <Accordion.Body>
                                    {groupedEntries[type]?.length > 0 ? (
                                        <ListGroup>
                                            {groupedEntries[type].map((entry, i) => (
                                                <ListGroup.Item
                                                    className={styles.entryItem}
                                                    key={i}
                                                    action
                                                    active={selectedEntry?.resource?.id === entry.resource?.id}
                                                    onClick={() => {
                                                        setSelectedEntry(entry);
                                                        console.log("Selected Entry:", entry);
                                                    }}
                                                >
                                                    <strong>{entry.resource?.id}</strong> –{" "}
                                                    {entry.title || entry.resource?.description || "No description"}
                                                    <br />
                                                    {formatLocalDate(entry.resource?.createdDateTime || entry.resource?.date || "NA")}
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    ) : (
                                        <p>No entries found with attachments for {type}.</p>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </Col>

                <Col md={6}>
                    <div
                        className="bg-light p-3 rounded"
                        style={{ maxHeight: "60vh", overflowY: "auto" }}
                    >
                        <AttachmentList
                            entry={selectedEntry}
                            selectedAttachments={selectedAttachments}
                            toggleSelected={toggleSelected}
                            fhirClient={fhirClient}
                        />
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default AttachmentBrowser;